/**
 * Sign-in token storage.
 *
 * The server also sets an HttpOnly session cookie, which is the safer of the
 * two and is preferred whenever it survives. This token exists because some
 * browsers were discarding that cookie, leaving sign-in impossible; it is
 * carried in a request header instead, where no cookie policy applies.
 *
 * Trade-off worth being explicit about: unlike the HttpOnly cookie, a token
 * in localStorage is readable by any script running on the page, so it offers
 * less protection against XSS. It is scoped to this origin, expires after 30
 * days server-side, and is revoked on sign-out.
 */
const STORAGE_KEY = 'mymdv.auth.token'

/** localStorage throws in some privacy modes — never let that break boot. */
function safeStorage(): Storage | null {
  try {
    const probe = '__mymdv_probe__'
    window.localStorage.setItem(probe, '1')
    window.localStorage.removeItem(probe)
    return window.localStorage
  } catch {
    return null
  }
}

let memoryFallback: string | null = null

export function getAuthToken(): string | null {
  const store = safeStorage()
  if (!store) return memoryFallback
  return store.getItem(STORAGE_KEY)
}

export function setAuthToken(token: string | null): void {
  memoryFallback = token
  const store = safeStorage()
  if (!store) return
  if (token) store.setItem(STORAGE_KEY, token)
  else store.removeItem(STORAGE_KEY)
}

export function clearAuthToken(): void {
  setAuthToken(null)
}
